import React, { useEffect, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";
import {
  Button,
  Container,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import AssessmentOutlinedIcon from "@material-ui/icons/AssessmentOutlined";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import CloseIcon from "@material-ui/icons/Close";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ForumOutlinedIcon from "@material-ui/icons/ForumOutlined";
import HourglassEmptyOutlinedIcon from "@material-ui/icons/HourglassEmptyOutlined";
import RefreshIcon from "@material-ui/icons/Refresh";
import ScheduleOutlinedIcon from "@material-ui/icons/ScheduleOutlined";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
  container: {
    maxWidth: 1480,
    paddingBottom: theme.spacing(4),
    paddingTop: theme.spacing(3)
  },
  pageHeader: {
    alignItems: "flex-end",
    display: "flex",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("xs")]: {
      alignItems: "flex-start",
      flexDirection: "column"
    }
  },
  eyebrow: {
    color: "#3154f5",
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: "0.1em",
    textTransform: "uppercase"
  },
  title: {
    fontWeight: 800,
    letterSpacing: 0,
    marginTop: theme.spacing(0.5)
  },
  subtitle: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.75)
  },
  filterBar: {
    alignItems: "center",
    border: `1px solid ${theme.palette.type === "dark" ? "#2c3b55" : "#e5ebf3"}`,
    borderRadius: 8,
    boxShadow: "none",
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2.5),
    padding: theme.spacing(1.5)
  },
  filter: {
    minWidth: 172,
    [theme.breakpoints.down("xs")]: { width: "100%" }
  },
  dateFilter: {
    minWidth: 154,
    [theme.breakpoints.down("xs")]: { width: "100%" }
  },
  refreshButton: { marginLeft: "auto" },
  metricCard: {
    border: `1px solid ${theme.palette.type === "dark" ? "#2c3b55" : "#e5ebf3"}`,
    borderRadius: 8,
    boxShadow: "none",
    minHeight: 132,
    padding: theme.spacing(2)
  },
  metricTop: { alignItems: "center", display: "flex", justifyContent: "space-between" },
  metricLabel: { color: theme.palette.text.secondary, fontSize: 13, fontWeight: 700 },
  metricValue: { fontSize: 30, fontWeight: 800, letterSpacing: 0, marginTop: theme.spacing(2) },
  metricDetail: { color: theme.palette.text.secondary, fontSize: 12, marginTop: theme.spacing(0.75) },
  panel: {
    border: `1px solid ${theme.palette.type === "dark" ? "#2c3b55" : "#e5ebf3"}`,
    borderRadius: 8,
    boxShadow: "none",
    minHeight: 276,
    overflow: "hidden"
  },
  panelHeader: { alignItems: "center", display: "flex", justifyContent: "space-between", padding: theme.spacing(1.5, 2) },
  panelTitle: { color: "#3154f5", fontSize: 15, fontWeight: 800 },
  panelSubtitle: { color: theme.palette.text.secondary, fontSize: 12, marginTop: theme.spacing(0.25) },
  panelContent: { borderTop: `1px solid ${theme.palette.divider}`, minHeight: 210, padding: theme.spacing(1.5, 2, 2) },
  chart: { height: 230, width: "100%" },
  empty: { alignItems: "center", color: theme.palette.text.secondary, display: "flex", height: 190, justifyContent: "center", textAlign: "center" },
  muted: { color: theme.palette.text.secondary, fontSize: 12, marginTop: theme.spacing(1) },
  tip: { color: theme.palette.text.secondary, fontSize: 12, marginTop: theme.spacing(1.5) }
}));

const dateToday = () => new Date().toLocaleDateString("en-CA");

const initialData = {
  overview: { total: 0, closed: 0, open: 0, pending: 0, answered: 0, withoutResponse: 0, averageFirstResponseSeconds: 0, averageResolutionSeconds: 0 },
  hourly: [],
  charts: { channels: [], connections: [], initiation: [], service: [], queues: [], contacts: [] },
  filters: { queues: [], whatsapps: [] }
};

const formatDuration = value => {
  const seconds = Number(value || 0);
  if (!seconds) return "Sin datos";
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  return `${Math.floor(minutes / 60)} h ${minutes % 60 ? `${minutes % 60} min` : ""}`.trim();
};

const EmptyState = () => <div className={useStyles().empty}>No hay datos disponibles para este período.</div>;

const ReportPanel = ({ title, subtitle, children, initialOpen = true }) => {
  const classes = useStyles();
  const [open, setOpen] = useState(initialOpen);
  return (
    <Paper className={classes.panel}>
      <div className={classes.panelHeader}>
        <div>
          <Typography className={classes.panelTitle}>{title}</Typography>
          {subtitle && <Typography className={classes.panelSubtitle}>{subtitle}</Typography>}
        </div>
        <IconButton aria-label={open ? `Contraer ${title}` : `Expandir ${title}`} onClick={() => setOpen(!open)} size="small">
          {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </div>
      {open && <div className={classes.panelContent}>{children}</div>}
    </Paper>
  );
};

const DistributionChart = ({ data }) => {
  const classes = useStyles();
  if (!data.length) return <EmptyState />;
  return (
    <div className={classes.chart}>
      <ResponsiveContainer>
        <BarChart data={data} layout="vertical" margin={{ top: 6, right: 8, left: 16, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis type="number" allowDecimals={false} />
          <YAxis type="category" dataKey="label" width={116} tick={{ fontSize: 12 }} />
          <Tooltip cursor={{ fill: "rgba(49,84,245,0.06)" }} />
          <Bar dataKey="total" name="Tickets" fill="#3154f5" radius={[0, 4, 4, 0]} maxBarSize={24} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

const MetricCard = ({ label, value, detail, icon, color }) => {
  const classes = useStyles();
  return (
    <Paper className={classes.metricCard}>
      <div className={classes.metricTop}>
        <Typography className={classes.metricLabel}>{label}</Typography>
        {React.cloneElement(icon, { style: { color } })}
      </div>
      <Typography className={classes.metricValue} style={{ color }}>{value}</Typography>
      <Typography className={classes.metricDetail}>{detail}</Typography>
    </Paper>
  );
};

const Reports = () => {
  const classes = useStyles();
  const [filters, setFilters] = useState({ from: dateToday(), to: dateToday(), queueId: "", whatsappId: "" });
  const [data, setData] = useState(initialData);
  const [loading, setLoading] = useState(true);

  const loadReports = async () => {
    setLoading(true);
    try {
      const params = { from: filters.from, to: filters.to };
      if (filters.queueId) params.queueId = filters.queueId;
      if (filters.whatsappId) params.whatsappId = filters.whatsappId;
      const response = await api.get("/reports/metrics", { params });
      setData({ ...initialData, ...response.data, overview: { ...initialData.overview, ...(response.data?.overview || {}) }, charts: { ...initialData.charts, ...(response.data?.charts || {}) }, filters: response.data?.filters || initialData.filters });
    } catch (err) {
      setData(initialData);
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadReports(); }, []);

  const updateFilter = field => event => setFilters(current => ({ ...current, [field]: event.target.value }));
  const hasData = data.overview.total > 0;

  return (
    <Container maxWidth={false} className={classes.container}>
      <div className={classes.pageHeader}>
        <Typography className={classes.subtitle}>Actividad, respuesta y distribución de tickets con datos del período seleccionado.</Typography>
      </div>
      <Paper className={classes.filterBar}>
        <TextField className={classes.dateFilter} label="Desde" type="date" value={filters.from} onChange={updateFilter("from")} InputLabelProps={{ shrink: true }} variant="outlined" size="small" />
        <TextField className={classes.dateFilter} label="Hasta" type="date" value={filters.to} onChange={updateFilter("to")} InputLabelProps={{ shrink: true }} variant="outlined" size="small" />
        <FormControl variant="outlined" size="small" className={classes.filter}>
          <InputLabel>Conexión</InputLabel>
          <Select value={filters.whatsappId} onChange={updateFilter("whatsappId")} label="Conexión">
            <MenuItem value="">Todas</MenuItem>
            {data.filters.whatsapps.map(item => <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl variant="outlined" size="small" className={classes.filter}>
          <InputLabel>Cola</InputLabel>
          <Select value={filters.queueId} onChange={updateFilter("queueId")} label="Cola">
            <MenuItem value="">Todas</MenuItem>
            {data.filters.queues.map(item => <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>)}
          </Select>
        </FormControl>
        <Button className={classes.refreshButton} color="primary" disabled={loading} onClick={loadReports} startIcon={<RefreshIcon />} variant="contained">Actualizar</Button>
      </Paper>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}><MetricCard label="Tickets creados" value={loading ? "-" : data.overview.total} detail={`${data.overview.open} en atención y ${data.overview.pending} en cola`} icon={<ForumOutlinedIcon />} color="#3154f5" /></Grid>
        <Grid item xs={12} sm={6} md={3}><MetricCard label="Tickets cerrados" value={loading ? "-" : data.overview.closed} detail="Estado actual de tickets creados en el período" icon={<CheckCircleOutlineIcon />} color="#16a34a" /></Grid>
        <Grid item xs={12} sm={6} md={3}><MetricCard label="Con respuesta" value={loading ? "-" : data.overview.answered} detail={`${data.overview.withoutResponse} sin respuesta saliente`} icon={<AssessmentOutlinedIcon />} color="#0284c7" /></Grid>
        <Grid item xs={12} sm={6} md={3}><MetricCard label="Sin respuesta" value={loading ? "-" : data.overview.withoutResponse} detail="Sin mensajes salientes del equipo" icon={<CloseIcon />} color="#dc2626" /></Grid>
        <Grid item xs={12} md={6}>
          <ReportPanel title="Actividad por hora" subtitle="Tickets creados y tickets con respuesta por hora.">
            {!hasData ? <EmptyState /> : <div className={classes.chart}><ResponsiveContainer><LineChart data={data.hourly} margin={{ top: 8, right: 16, left: -18, bottom: 0 }}><CartesianGrid strokeDasharray="3 3" vertical={false} /><XAxis dataKey="hour" tick={{ fontSize: 11 }} interval={2} /><YAxis allowDecimals={false} /><Tooltip /><Line type="monotone" dataKey="created" name="Tickets creados" stroke="#3154f5" strokeWidth={2} dot={false} /><Line type="monotone" dataKey="answered" name="Con respuesta" stroke="#42b883" strokeWidth={2} dot={false} /></LineChart></ResponsiveContainer></div>}
          </ReportPanel>
        </Grid>
        <Grid item xs={12} md={6}>
          <ReportPanel title="Tiempos de atención" subtitle="Calculados desde los mensajes y el último cambio del ticket cerrado.">
            {!hasData ? <EmptyState /> : <Grid container spacing={2}><Grid item xs={12} sm={6}><MetricCard label="Primera respuesta" value={formatDuration(data.overview.averageFirstResponseSeconds)} detail="Desde el primer mensaje del cliente" icon={<ScheduleOutlinedIcon />} color="#3154f5" /></Grid><Grid item xs={12} sm={6}><MetricCard label="Resolución estimada" value={formatDuration(data.overview.averageResolutionSeconds)} detail="Desde la creación hasta el último cierre" icon={<HourglassEmptyOutlinedIcon />} color="#d97706" /></Grid></Grid>}
          </ReportPanel>
        </Grid>
        <Grid item xs={12} md={6}><ReportPanel title="Chats por canal"><DistributionChart data={data.charts.channels} /></ReportPanel></Grid>
        <Grid item xs={12} md={6}><ReportPanel title="Chats por conexión"><DistributionChart data={data.charts.connections} /></ReportPanel></Grid>
        <Grid item xs={12} md={6}><ReportPanel title="Tipo de iniciación"><DistributionChart data={data.charts.initiation} /></ReportPanel></Grid>
        <Grid item xs={12} md={6}><ReportPanel title="Estado de atención"><DistributionChart data={data.charts.service} /></ReportPanel></Grid>
        <Grid item xs={12} md={6}><ReportPanel title="Top 5 colas"><DistributionChart data={data.charts.queues} /></ReportPanel></Grid>
        <Grid item xs={12} md={6}><ReportPanel title="Top 10 contactos"><DistributionChart data={data.charts.contacts} /></ReportPanel></Grid>
      </Grid>
      <Typography className={classes.tip}>La resolución se estima con la fecha de última actualización de los tickets cerrados porque la plataforma no conserva un historial de cambios de estado.</Typography>
    </Container>
  );
};

export default Reports;
